import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, Select, Modal, Tooltip } from "antd";
import { BASE_API_URL } from "../../../../utils/consts";
import { usePermissions } from "../../../../utils/usePermissions";
import { isSuperAdmin } from "../../../../utils/permissions";
import DashboardAIAnalysis from "./DashboardAIAnalysis";
import { GoodsInventoryEntry } from "../../../../utils/api/goodsInventoryAPI";
import { useGetGoodsInventoryQuery, useGetSalarySummaryBulkQuery } from "../../../../utils/redux/api/financeApi";
import { getAptIncomes, getAptExpenses } from "../../../../utils/api/apartmentRentalsAPI";
import { isLocationVisibleForPeriod } from "../../../../utils/helpers/locationVisibility";
import "./DashboardOverview.scss";

// ─── Types ────────────────────────────────────────────────────────────────────

interface MonthData {
  month: string;
  label: string;
  full: string;
  prihod: number;
  trosak: number;
  profit: number;
  investicije: number;
  krediti: number;
  finalProfit: number;
  investicijeDetail: { name: string; amount: number }[];
  kreditiDetail: { name: string; amount: number }[];
  categories?: { direct: CategoryItem[]; operating: CategoryItem[] };
  salaries?: SalaryItem[];
  margin: number;
  finalMargin: number;
  current: boolean;
}
interface CategoryItem { id: string; name: string; total: number }
interface SalaryItem {
  userId: string; name: string; position: string | null; total: number;
  fixed: number; bonus: number; overtime: number; percentage: number; daily: number;
  penalty: number; loanDeduction: number; payoutAmount: number;
}
interface DetailItem {
  name: string; amount: number; hasRecord?: boolean; installmentNumber?: number; installmentsCount?: number;
  // Present for credits in a foreign currency: `amount` is already converted
  // to the base currency, originalAmount/originalCurrency show the loan as-is.
  originalAmount?: number; originalCurrency?: string; rateMissing?: boolean;
}
interface DiscrepancyRecord { date: string; location: string | null; byType: number; byPayment: number; diff: number }
interface FinanceDetail {
  month: string;
  revenue: {
    byType: { name: string; amount: number }[];
    byTypeTotal: number;
    byPayment: { gotovina: number; internaGotovina: number; kartica: number; dostava: number; prenos: number; drugo: number; total: number };
    extraIncome: number;
    totalWithExtra: number;
    discrepancy: number;
    discrepancyRecords: DiscrepancyRecord[];
  };
  expenses: {
    direct: CategoryItem[];
    operating: CategoryItem[];
    directTotal: number;
    operatingTotal: number;
    total: number;
  };
  investments: DetailItem[];
  credits: DetailItem[];
  rashod: { investicije: number; krediti: number; total: number };
  profit: number;
  finalProfit: number;
}
interface ApartmentMonthData {
  month: string; label: string; full: string;
  revenue: number; paidAmount: number; count: number;
  channels: Record<string, number>;
}
interface ApartmentRevenueData {
  months: ApartmentMonthData[];
  total: number;
  hasData: boolean;
}

interface FinanceData {
  months: MonthData[];
  categories: { direct: CategoryItem[]; operating: CategoryItem[] };
  activeUsersCount: number;
  loansThisMonth: { count: number; total: number };
  detail?: FinanceDetail;
  baseCurrency?: string; // ISO code (e.g. "RSD"), for comparing against credit.originalCurrency
}
interface ExpiringItem {
  userId: string;
  firstName: string;
  lastName: string;
  type: "contract" | "sanitary";
  dueDate: string;
  daysUntil: number;
  position?: string;
}
interface CompanyInfo {
  birthdays?: { firstName: string; lastName: string; birthday?: string }[];
  upcomingExpirations?: ExpiringItem[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  Math.round(n).toLocaleString("sr-RS");

const fmtK = (n: number) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".", ",") + "M";
  if (n >= 1_000) return Math.round(n / 1_000) + "K";
  return String(Math.round(n));
};

function buildSparkPath(values: number[], w = 120, h = 32) {
  if (values.length < 2) return null;
  const mn = Math.min(...values);
  const mx = Math.max(...values);
  const pad = (mx - mn) * 0.15 || 1;
  const yS = (v: number) => h - 4 - ((v - mn + pad) / (mx - mn + pad * 2)) * (h - 8);
  const xS = (i: number) => (i / (values.length - 1)) * (w - 4) + 2;
  const pts = values.map((v, i) => ({ x: xS(i), y: yS(v) }));
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const area =
    `M${pts[0].x.toFixed(1)},${h} ` +
    pts.map((p) => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ") +
    ` L${pts[pts.length - 1].x.toFixed(1)},${h} Z`;
  return { line, area, pts };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function InitialsAvatar({ name, size = 28, colors }: { name: string; size?: number; colors?: string }) {
  const [first, last] = name.split(" ");
  const initials = `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();
  const palette = colors ?? "linear-gradient(135deg,oklch(0.6 0.16 25),oklch(0.5 0.18 350))";
  return (
    <div
      className="dov-ava"
      style={{ width: size, height: size, fontSize: size * 0.38, background: palette }}
    >
      {initials}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  companyInfo?: CompanyInfo;
}

const DashboardOverview: React.FC<Props> = ({ companyInfo }) => {
  const userData = useSelector((state: any) => state.userData.user);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const superAdmin = isSuperAdmin(userData?.roles);

  // Permission flags — mirrors RevenueStatistics permission pattern
  const canViewDashboard  = superAdmin || hasPermission("dashboard:view");
  const canViewRevenue    = superAdmin || hasPermission("dashboard:total-revenue");
  const canViewBreakdown  = superAdmin || hasPermission("dashboard:breakdown");  // KPIs + monthly table
  const canViewCharts     = superAdmin || hasPermission("dashboard:charts");     // big chart
  const canViewIncome     = superAdmin || hasPermission("dashboard:revenue-stats-income");
  const canViewExpenses   = superAdmin || hasPermission("dashboard:revenue-stats-expenses");
  const canViewProfit     = superAdmin || hasPermission("dashboard:revenue-stats-profit");
  const canViewPeople     = superAdmin || hasPermission("dashboard:people-insights");

  // Client currency — uses currencyIcon if set, derives from currency name otherwise
  const currency: string = userData?.client?.currencyIcon
    || (() => {
      switch (userData?.client?.currency) {
        case "Dinar":  return "RSD";
        case "Euro":   return "€";
        case "Dollar": return "$";
        default:       return "RSD";
      }
    })();

  // Per-client toggle (Settings → General) — off by default, must be explicitly enabled
  const showEmployeeSalaries = !!userData?.client?.settings?.showEmployeeSalariesInDashboard;

  const [period, setPeriod] = useState(6);
  const [customMonths, setCustomMonths] = useState<string[]>([]); // YYYY-MM strings for custom mode
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [finance, setFinance] = useState<FinanceData | null>(null);
  const [loadingFinance, setLoadingFinance] = useState(false);
  const [hoveredMonth, setHoveredMonth] = useState<number | null>(null);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });
  const chartSvgRef = useRef<SVGSVGElement>(null);
  const [inclInvestments, setInclInvestments] = useState(true);
  const [inclCredits, setInclCredits] = useState(true);
  // Location + month filter
  const [locations, setLocations] = useState<{ _id: string; name: string }[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(null); // null = current month
  // Statistics detail month (null = follow chart's last month)
  const [statDetailMonth, setStatDetailMonth] = useState<string | null>(null);
  const [statDetail, setStatDetail] = useState<FinanceDetail | null>(null);
  const [loadingStatDetail, setLoadingStatDetail] = useState(false);
  // Keep the top period nav and the Statistika detail nav pointing at the same month
  const changeMonth = (val: Dayjs) => {
    setSelectedMonth(val.isSame(dayjs(), "month") ? null : val);
    setStatDetailMonth(val.format("YYYY-MM"));
  };
  // Discrepancy modal
  const [discrepancyOpen, setDiscrepancyOpen] = useState(false);
  // Apartment revenue (separate endpoint)
  const [apartmentRevenue, setApartmentRevenue] = useState<ApartmentRevenueData | null>(null);
  // Apartment additional income/expenses (Finansije), bucketed by "YYYY-MM" — folded into apartment revenue
  const [aptExtraByMonth, setAptExtraByMonth] = useState<Record<string, { income: number; expense: number }>>({});
  const [revenueTypes, setRevenueTypes] = useState<{ _id: string; name: string }[]>([]);

  // ── Fetch locations ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!userData?.clientId) return;
    axios
      .get(`${BASE_API_URL}/${userData.clientId}/locations`)
      .then((res) => setLocations(res.data || []))
      .catch(() => {});
  }, [userData?.clientId]);

  // ── Active months based on current period selection ──────────────────────
  const dashboardActiveMonths = useMemo(() => {
    if (isCustomMode && customMonths.length > 0) return customMonths;
    const endM = selectedMonth ?? dayjs();
    return Array.from({ length: period }, (_, i) =>
      endM.subtract(period - 1 - i, "month").format("YYYY-MM"),
    );
  }, [isCustomMode, customMonths, selectedMonth, period]);

  const visibleLocations = useMemo(() => {
    return locations.filter((loc: any) => isLocationVisibleForPeriod(loc, dashboardActiveMonths));
  }, [locations, dashboardActiveMonths]);

  // ── Fetch dashboard finance + apartment revenue (parallel) ───────────────
  useEffect(() => {
    if (!userData?.clientId) return;
    if (isCustomMode && customMonths.length === 0) return;
    setLoadingFinance(true);

    const params = new URLSearchParams();
    if (isCustomMode) {
      params.set("specificMonths", customMonths.join(","));
    } else {
      params.set("months", String(period));
      const endMonth = selectedMonth ? selectedMonth.format("YYYY-MM") : undefined;
      if (endMonth) params.set("endMonth", endMonth);
    }
    if (selectedLocation) params.set("locationId", selectedLocation);

    const sortedMonths = [...dashboardActiveMonths].sort();
    const extraFrom = sortedMonths.length ? dayjs(sortedMonths[0], "YYYY-MM").startOf("month").toISOString() : undefined;
    const extraTo = sortedMonths.length ? dayjs(sortedMonths[sortedMonths.length - 1], "YYYY-MM").endOf("month").toISOString() : undefined;

    Promise.all([
      axios.get(`${BASE_API_URL}/${userData.clientId}/dashboard-finance?${params}`),
      axios.get(`${BASE_API_URL}/${userData.clientId}/dashboard-apartment-revenue?${params}`)
        .catch(() => ({ data: null })), // non-critical — ignore if no apartments
      Promise.all([
        getAptIncomes(userData.clientId, { from: extraFrom, to: extraTo }),
        getAptExpenses(userData.clientId, { from: extraFrom, to: extraTo }),
      ]).catch(() => null), // non-critical — ignore if no apartments
    ])
      .then(([finRes, aptRes, extraRes]) => {
        setFinance(finRes.data);
        if (aptRes.data?.hasData) setApartmentRevenue(aptRes.data);
        else setApartmentRevenue(null);

        const map: Record<string, { income: number; expense: number }> = {};
        if (extraRes) {
          const [incRes, expRes] = extraRes;
          incRes.data.forEach((i) => {
            const key = dayjs(i.date).format("YYYY-MM");
            if (!map[key]) map[key] = { income: 0, expense: 0 };
            map[key].income += i.amount || 0;
          });
          expRes.data.forEach((e) => {
            const key = dayjs(e.date).format("YYYY-MM");
            if (!map[key]) map[key] = { income: 0, expense: 0 };
            map[key].expense += e.amount || 0;
          });
        }
        setAptExtraByMonth(map);
      })
      .catch(() => {})
      .finally(() => setLoadingFinance(false));
  }, [userData?.clientId, period, selectedLocation, selectedMonth, isCustomMode, customMonths, dashboardActiveMonths]);

  // ── Fetch revenue types once ─────────────────────────────────────────
  useEffect(() => {
    if (!userData?.clientId) return;
    axios
      .get(`${BASE_API_URL}/admin/${userData.clientId}/getRevenueTypes`)
      .then((res) => setRevenueTypes(res.data || []))
      .catch(() => {});
  }, [userData?.clientId]);

  // ── Goods inventory for chart months ──────────────────────────────────
  const chartMonths = finance?.months?.map((m: MonthData) => m.month) ?? [];
  const goodsInventoryMonths = useMemo(() => {
    if (chartMonths.length === 0) return "";
    // Include previous month for openingStock of earliest chart month
    const earliest = [...chartMonths].sort()[0];
    const prev = dayjs(earliest, "YYYY-MM").subtract(1, "month").format("YYYY-MM");
    return [prev, ...chartMonths].join(",");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartMonths.join(",")]);

  const { data: goodsInventoryData } = useGetGoodsInventoryQuery(
    { clientId: userData?.clientId || "", months: goodsInventoryMonths, locationId: selectedLocation || undefined },
    { skip: !userData?.clientId || !goodsInventoryMonths },
  );
  const goodsInventory: GoodsInventoryEntry[] = goodsInventoryData?.data || [];

  // ── When main finance loads, auto-init statDetailMonth to last chart month ─
  useEffect(() => {
    if (!finance?.months?.length) return;
    const lastMonth = finance.months[finance.months.length - 1]?.month;
    if (lastMonth && !statDetailMonth) setStatDetailMonth(lastMonth);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finance?.months]);

  // ── When statDetailMonth differs from finance.detail, fetch separate detail ─
  useEffect(() => {
    const defaultMonth = finance?.months?.[finance.months.length - 1]?.month;
    if (!statDetailMonth || statDetailMonth === defaultMonth || !userData?.clientId) {
      setStatDetail(null);
      return;
    }
    setLoadingStatDetail(true);
    const params = new URLSearchParams();
    params.set("months", "1");
    params.set("endMonth", statDetailMonth);
    if (selectedLocation) params.set("locationId", selectedLocation);
    axios
      .get(`${BASE_API_URL}/${userData.clientId}/dashboard-finance?${params}`)
      .then((res) => setStatDetail(res.data?.detail ?? null))
      .catch(() => setStatDetail(null))
      .finally(() => setLoadingStatDetail(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statDetailMonth, userData?.clientId, selectedLocation]);

  // ── Salary total for the detail month (only if client enabled the feature) ─
  const salaryDetailMonth = statDetail?.month ?? finance?.detail?.month;
  const salaryStartDate = salaryDetailMonth
    ? dayjs(salaryDetailMonth, "YYYY-MM").startOf("month").format("YYYY-MM-DD")
    : "";
  const salaryEndDate = salaryDetailMonth
    ? dayjs(salaryDetailMonth, "YYYY-MM").endOf("month").format("YYYY-MM-DD")
    : "";

  const { data: salarySummaryData } = useGetSalarySummaryBulkQuery(
    { clientId: userData?.clientId || "", startDate: salaryStartDate, endDate: salaryEndDate },
    { skip: !showEmployeeSalaries || !userData?.clientId || !salaryDetailMonth },
  );

  const { salaryTotal, salaryActiveCount, salaryFrozenCount } = useMemo(() => {
    let summaries = salarySummaryData?.summaries ?? [];
    if (selectedLocation) {
      summaries = summaries.filter((s: any) => {
        const loc = s.user?.location?._id || s.user?.location;
        return loc && loc.toString() === selectedLocation;
      });
    }
    const total = summaries.reduce(
      (sum: number, { salarySummary }: any) => sum + (salarySummary?.totalForMonth ?? 0),
      0,
    );
    const withSalary = summaries.filter((s: any) => (s.salarySummary?.totalForMonth ?? 0) > 0);
    return {
      salaryTotal: total,
      salaryActiveCount: withSalary.filter((s: any) => !s.user?.isFrozen).length,
      salaryFrozenCount: withSalary.filter((s: any) => s.user?.isFrozen).length,
    };
  }, [salarySummaryData, selectedLocation]);

  const months: MonthData[] = finance?.months ?? [];

  // ── Apartment revenue merged per month ───────────────────────────────────
  // Build map for fast lookup: monthKey → apartment revenue
  const aptRevenueByMonth = useMemo(() => {
    if (!apartmentRevenue?.months) return {};
    const map: Record<string, number> = {};
    apartmentRevenue.months.forEach((m) => {
      const extra = aptExtraByMonth[m.month] || { income: 0, expense: 0 };
      map[m.month] = m.revenue + extra.income - extra.expense;
    });
    return map;
  }, [apartmentRevenue, aptExtraByMonth]);

  // Effective combined prihod per month (base + apartments)
  const monthsWithApt = useMemo(() =>
    months.map((m) => ({
      ...m,
      prihodApt: aptRevenueByMonth[m.month] || 0,
      prihodTotal: m.prihod + (aptRevenueByMonth[m.month] || 0),
    })),
    [months, aptRevenueByMonth],
  );

  // ── KPI totals ───────────────────────────────────────────────────────────
  const sumPrihod = useMemo(() => monthsWithApt.reduce((s, m) => s + m.prihodTotal, 0), [monthsWithApt]);
  const sumTrosak      = useMemo(() => months.reduce((s, m) => s + m.trosak, 0), [months]);
  const sumInvesticije = useMemo(() => months.reduce((s, m) => s + (m.investicije || 0), 0), [months]);
  const sumKrediti     = useMemo(() => months.reduce((s, m) => s + (m.krediti || 0), 0), [months]);

  // Effective profit based on checkbox selection (includes apartment revenue)
  const effectiveProfit = useMemo(() =>
    monthsWithApt.reduce((s, m) => {
      const prihodTotal = m.prihodTotal;
      const profit = prihodTotal - m.trosak;
      const inv  = inclInvestments ? (m.investicije || 0) : 0;
      const kred = inclCredits     ? (m.krediti || 0)     : 0;
      return s + profit - inv - kred;
    }, 0),
    [monthsWithApt, inclInvestments, inclCredits],
  );

  const sumProfit = useMemo(() =>
    monthsWithApt.reduce((s, m) => s + (m.prihodTotal - m.trosak), 0),
    [monthsWithApt],
  );
  const avgMargin = useMemo(
    () => (months.length ? months.reduce((s, m) => s + m.margin, 0) / months.length : 0),
    [months],
  );
  const avgFinalMargin = useMemo(
    () => sumPrihod > 0 ? effectiveProfit / sumPrihod : 0,
    [effectiveProfit, sumPrihod],
  );

  // ── Goods inventory — non-chart computations (must be before kpis) ───
  const goodsClosingStockByMonth = useMemo(() => {
    const map: Record<string, number> = {};
    goodsInventory.forEach((e) => {
      const key = `${e.year}-${String(e.month).padStart(2, "0")}`;
      map[key] = (map[key] || 0) + e.closingStock;
    });
    return map;
  }, [goodsInventory]);

  const latestChartMonth = monthsWithApt[monthsWithApt.length - 1]?.month ?? "";
  const prevLatestMonth = latestChartMonth
    ? dayjs(latestChartMonth, "YYYY-MM").subtract(1, "month").format("YYYY-MM")
    : "";
  const currentGoodsStock = goodsClosingStockByMonth[latestChartMonth] ?? 0;
  const prevGoodsStock = goodsClosingStockByMonth[prevLatestMonth] ?? 0;
  const goodsStockDelta = prevGoodsStock > 0
    ? ((currentGoodsStock - prevGoodsStock) / prevGoodsStock) * 100
    : null;
  const goodsStockVals = monthsWithApt.map((m) => goodsClosingStockByMonth[m.month] || 0);
  const hasGoodsData = goodsStockVals.some((v) => v > 0);

  const kpis = [
    {
      label: "Prihod", sublabel: `${isCustomMode ? "custom" : period + "m"}`, value: sumPrihod, isRSD: true,
      delta: monthsWithApt.length >= 2 ? ((monthsWithApt[monthsWithApt.length - 1].prihodTotal - monthsWithApt[0].prihodTotal) / (monthsWithApt[0].prihodTotal || 1)) * 100 : null,
      spark: monthsWithApt.map((m) => m.prihodTotal), color: "oklch(0.55 0.16 265)", gradId: "dov-g-prihod",
    },
    {
      label: "Troškovi", sublabel: `${period}m`, value: sumTrosak, isRSD: true,
      delta: months.length >= 2 ? ((months[months.length - 1].trosak - months[0].trosak) / (months[0].trosak || 1)) * 100 : null,
      spark: months.map((m) => m.trosak), color: "oklch(0.72 0.09 22)", gradId: "dov-g-trosak",
    },
    {
      label: "Profit (op.)", sublabel: `${period}m`, value: sumProfit, isRSD: true,
      delta: months.length >= 2 ? ((months[months.length - 1].profit - months[0].profit) / (Math.abs(months[0].profit) || 1)) * 100 : null,
      spark: months.map((m) => m.profit), color: "oklch(0.62 0.13 158)", gradId: "dov-g-profit",
      tooltip: "Prihod − Direktni − Operativni troškovi",
    },
    {
      label: "Finalni profit", sublabel: `${period}m`, value: effectiveProfit, isRSD: true,
      delta: null,
      spark: monthsWithApt.map((m) => {
        const inv  = inclInvestments ? (m.investicije || 0) : 0;
        const kred = inclCredits     ? (m.krediti || 0)     : 0;
        return (m.prihodTotal - m.trosak) - inv - kred;
      }),
      color: effectiveProfit >= 0 ? "oklch(0.55 0.18 305)" : "oklch(0.62 0.18 22)",
      gradId: "dov-g-final",
      tooltip: "Profit − Investicije − Krediti (prema checkboxovima)",
    },
    ...(hasGoodsData ? [{
      label: "Vrijednost robe", sublabel: `${dayjs(latestChartMonth, "YYYY-MM").format("MMM YY")}`, value: currentGoodsStock, isRSD: true,
      delta: goodsStockDelta,
      spark: goodsStockVals,
      color: "oklch(0.62 0.12 200)",
      gradId: "dov-g-goods",
      tooltip: "Ukupno krajnje stanje robe za tekući mesec",
    }] : []),
  ];

  // ── Chart geometry ───────────────────────────────────────────────────────
  const W = 920, H = 300, padL = 56, padR = 24, padT = 18, padB = 40;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const maxVal = Math.max(...monthsWithApt.map((m) => Math.max(m.prihodTotal, m.trosak)), 1) * 1.12;
  const yS = (v: number) => padT + innerH - (v / maxVal) * innerH;
  const colW = months.length ? innerW / months.length : innerW;
  const barW = Math.max((colW - 14) / 2, 4);
  const yTicks = [0, 1_000_000, 2_000_000, 3_000_000, 4_000_000, 5_000_000].filter(
    (t) => t <= maxVal * 1.05,
  );

  // Profit line points
  const profitPts = monthsWithApt.map((m, i) => ({
    x: padL + colW * i + colW / 2,
    y: yS(Math.max(0, m.prihodTotal - m.trosak)),
  }));
  const profitLine = profitPts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
  const profitArea =
    profitPts.length >= 2
      ? `M${profitPts[0].x},${yS(0)} ` +
        profitPts.map((p) => `L${p.x},${p.y}`).join(" ") +
        ` L${profitPts[profitPts.length - 1].x},${yS(0)} Z`
      : "";

  const finalProfitPts = monthsWithApt.map((m, i) => {
    const inv  = inclInvestments ? (m.investicije || 0) : 0;
    const kred = inclCredits     ? (m.krediti || 0)     : 0;
    const val  = (m.prihodTotal - m.trosak) - inv - kred;
    return { x: padL + colW * i + colW / 2, y: yS(Math.max(0, val)), val };
  });
  const finalProfitLine = finalProfitPts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");

  // ── Goods stock chart points (depend on yS / colW from chart geometry) ─
  const goodsStockPts = monthsWithApt.map((m, i) => ({
    x: padL + colW * i + colW / 2,
    y: yS(Math.max(0, goodsClosingStockByMonth[m.month] || 0)),
    val: goodsClosingStockByMonth[m.month] || 0,
  }));
  const goodsStockLine = goodsStockPts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");

  // Stat section: goods stock value for detail month
  const detailMonth = statDetail?.month ?? finance?.detail?.month;
  const detailGoodsStock = useMemo(
    () => (detailMonth ? (goodsClosingStockByMonth[detailMonth] ?? 0) : 0),
    [detailMonth, goodsClosingStockByMonth],
  );

  // Best month + trend
  const bestMonth = months.length
    ? months.reduce((a, b) => (a.profit > b.profit ? a : b))
    : null;
  const trend =
    months.length >= 2 && months[0].profit !== 0
      ? ((months[months.length - 1].profit - months[0].profit) / Math.abs(months[0].profit)) * 100
      : null;

  // ── Reminders strip data ─────────────────────────────────────────────────
  const contracts = (companyInfo?.upcomingExpirations ?? []).filter((i) => i.type === "contract");
  const sanitary = (companyInfo?.upcomingExpirations ?? []).filter((i) => i.type === "sanitary");
  const birthdays = companyInfo?.birthdays ?? [];

  // ── Export CSV ───────────────────────────────────────────────────────────
  const handleExport = () => {
    if (!months.length) return;
    const headers = ["Mesec", "Prihod", "Trošak", "Profit", "Margina"];
    const rows = months.map((m) => [
      m.full, m.prihod, m.trosak, m.profit, `${Math.round(m.margin * 100)}%`,
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
      .join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dashboard_${dayjs().format("YYYY-MM")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Last 24 months as options for custom multi-select
  const monthOptions = useMemo(() => {
    const opts = [];
    const now = dayjs();
    for (let i = 0; i < 24; i++) {
      const m = now.subtract(i, "month");
      opts.push({ value: m.format("YYYY-MM"), label: m.format("MMM YYYY") });
    }
    return opts;
  }, []);

  // Build Finance URL with current filters (location, period, optional category)
  // monthsOverride: pass specific months (e.g. [d.month] for single-month detail view)
  const buildFinanceUrl = (categoryId?: string, monthsOverride?: string[]) => {
    const params = new URLSearchParams();
    if (monthsOverride) {
      params.set("months", monthsOverride.join(","));
    } else if (isCustomMode && customMonths.length > 0) {
      params.set("months", customMonths.join(","));
    } else {
      // Pass all months in the period so Finance can compare multiple months
      const endM = selectedMonth ?? dayjs();
      const allMonths = Array.from({ length: period }, (_, i) =>
        endM.subtract(period - 1 - i, "month").format("YYYY-MM"),
      );
      params.set("months", allMonths.join(","));
    }
    if (selectedLocation) params.set("locations", selectedLocation);
    if (categoryId) params.set("category", categoryId);
    return `/finance?${params.toString()}`;
  };

  const navigateToFinance = (categoryId?: string, monthsOverride?: string[]) =>
    navigate(buildFinanceUrl(categoryId, monthsOverride));
  const openFinanceInNewTab = (categoryId?: string, monthsOverride?: string[]) =>
    window.open(buildFinanceUrl(categoryId, monthsOverride), "_blank");

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
    <div className="dov-wrap">
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="dov-pg-head">
        <div className="dov-greet">
          <div>
            <h1>Zdravo, {userData?.firstName}</h1>
            <div className="dov-greet-sub">
              {selectedLocation
                ? visibleLocations.find((l) => l._id === selectedLocation)?.name ?? "Lokacija"
                : "Sve lokacije"}
              {" · "}
              {isCustomMode
                ? customMonths.length > 0
                  ? customMonths.map((m) => dayjs(m).format("MMM YY")).join(", ")
                  : "Custom"
                : `zadnjih ${period} mjeseci`}
            </div>
          </div>
          {!isCustomMode && (
            <>
              <span className="dov-dot" style={{ alignSelf: "center" }} />
              <div className="dov-greet-month">
                {(selectedMonth ?? dayjs()).format("MMMM YYYY")}
              </div>
            </>
          )}
        </div>
        <div className="dov-tools">
          {/* Month navigator with prev/next (hidden in custom range mode) */}
          {!isCustomMode && (
            <div className="dov-month-nav">
              <button
                className="dov-month-nav-arrow"
                onClick={() => changeMonth((selectedMonth ?? dayjs()).subtract(1, "month"))}
              >
                ‹
              </button>
              <DatePicker
                picker="month"
                value={selectedMonth ?? dayjs()}
                onChange={(val) => {
                  if (!val) return;
                  changeMonth(val);
                }}
                allowClear={false}
                className="dov-month-nav-picker"
                format="MMMM YYYY"
                disabledDate={(d) => d.isAfter(dayjs(), "month")}
                suffixIcon={null}
              />
              <button
                className="dov-month-nav-arrow"
                disabled={(selectedMonth ?? dayjs()).isSame(dayjs(), "month")}
                onClick={() => {
                  const next = (selectedMonth ?? dayjs()).add(1, "month");
                  if (!next.isAfter(dayjs(), "month")) {
                    changeMonth(next);
                  }
                }}
              >
                ›
              </button>
            </div>
          )}

          {/* Location filter */}
          {visibleLocations.length > 0 && (
            <Select
              value={selectedLocation}
              onChange={setSelectedLocation}
              placeholder="Sve lokacije"
              allowClear
              style={{ minWidth: 160 }}
              className="dov-loc-select"
              size="middle"
            >
              {visibleLocations.map((loc) => (
                <Select.Option key={loc._id} value={loc._id}>
                  {loc.name}
                </Select.Option>
              ))}
            </Select>
          )}

          <button className="dov-btn-ghost" onClick={handleExport}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <path d="M12 4v12M6 12l6 6 6-6M4 20h16" />
            </svg>
            Izvezi
          </button>
        </div>
      </div>

      {/* ── DETALJNA STATISTIKA (kao stara Statistika prihoda) ───────────── */}
      {finance?.detail && (canViewIncome || canViewExpenses || canViewProfit) && (() => {
        const d = statDetail ?? finance.detail;
        const revenueTotal = d.revenue.totalWithExtra;
        const rashodTotal  = (inclInvestments ? (d.rashod?.investicije ?? 0) : 0) + (inclCredits ? (d.rashod?.krediti ?? 0) : 0);
        // Realne (neto) plate zaposlenih bez pozajmnica — salary-summary/bulk već vraća totalForMonth
        // kao bruto iznos isplate umanjen za pozajmnice (loanDeduction je isključen na backendu).
        // Opt-in po klijentu (Settings → General) — dok nije uključeno, ne ulazi u obračun.
        const salaryCost = showEmployeeSalaries ? salaryTotal : 0;
        const expensesGrandTotal = d.expenses.total + salaryCost;
        const netProfit    = d.profit - salaryCost;
        const finalProfit  = netProfit - rashodTotal;

        return (
          <div className="dov-stat-wrap">
            <div className="dov-stat-head">
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <h2 className="dov-stat-title">Statistika</h2>
                <div className="dov-month-nav" style={{ marginLeft: 4 }}>
                  <button
                    className="dov-month-nav-arrow"
                    onClick={() => {
                      const cur = statDetailMonth ?? months[months.length - 1]?.month;
                      if (cur) changeMonth(dayjs(cur, "YYYY-MM").subtract(1, "month"));
                    }}
                  >‹</button>
                  <span style={{ padding: "0 10px", fontSize: 14, fontWeight: 650, color: "var(--dov-ink)", minWidth: 110, textAlign: "center" }}>
                    {loadingStatDetail ? "…" : (statDetailMonth ? dayjs(statDetailMonth, "YYYY-MM").format("MMMM YYYY") : months[months.length - 1]?.full)}
                  </span>
                  <button
                    className="dov-month-nav-arrow"
                    onClick={() => {
                      const cur = statDetailMonth ?? months[months.length - 1]?.month;
                      if (cur) changeMonth(dayjs(cur, "YYYY-MM").add(1, "month"));
                    }}
                    disabled={(statDetailMonth ?? months[months.length - 1]?.month ?? "") >= dayjs().format("YYYY-MM")}
                  >›</button>
                </div>
                {visibleLocations.length > 0 && (
                  <Select
                    value={selectedLocation}
                    onChange={setSelectedLocation}
                    placeholder="Sve lokacije"
                    allowClear
                    size="small"
                    style={{ minWidth: 150 }}
                    className="dov-loc-select dov-stat-loc-select"
                    options={visibleLocations.map((loc) => ({ label: loc.name, value: loc._id }))}
                  />
                )}
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button className="dov-btn-ghost" onClick={() => navigateToFinance()}>
                  Otvori finansije →
                </button>
                <button
                  className="dov-btn-ghost"
                  title="Otvori u novom tabu"
                  onClick={() => openFinanceInNewTab()}
                  style={{ padding: "6px 8px" }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="dov-stat-grid">
              {/* ── Prihodi ─────────────────────────────────────────────── */}
              {canViewIncome && <div className="dov-stat-panel">
                <div className="dov-stat-panel-head accent">Prihodi</div>
                <div className="dov-stat-body">
                  {(d.revenue.byType?.length ?? 0) > 0 && (
                    <div className="dov-stat-group">
                      <div className="dov-stat-group-lbl">Prihodi po grupama</div>
                      {(d.revenue.byType ?? []).map((t) => (
                        <div key={t.name} className="dov-stat-row">
                          <span className="dov-stat-lbl">{t.name}</span>
                          <span className="dov-stat-bar-wrap">
                            <span className="dov-stat-bar">
                              <span style={{ width: `${revenueTotal > 0 ? Math.round(t.amount / revenueTotal * 100) : 0}%`, background: "var(--dov-accent)" }} />
                            </span>
                            <span className="dov-stat-pct">{revenueTotal > 0 ? (t.amount / revenueTotal * 100).toFixed(2) : "0.00"}%</span>
                          </span>
                          <span className="dov-stat-val mono">{fmt(t.amount)}</span>
                        </div>
                      ))}
                      <div className="dov-stat-subtotal">
                        <span>Prihodi po grupama</span>
                        <span className="mono">{fmt((d.revenue.byType ?? []).reduce((s, t) => s + t.amount, 0))}</span>
                      </div>
                    </div>
                  )}

                  <div className="dov-stat-group">
                    <div className="dov-stat-group-lbl">Način plaćanja</div>
                    {[
                      { label: "Gotovina", val: d.revenue.byPayment.gotovina },
                      { label: "Kartica", val: d.revenue.byPayment.kartica },
                      { label: "Prenos na račun", val: d.revenue.byPayment.prenos },
                      { label: "Interna gotovina", val: d.revenue.byPayment.internaGotovina },
                      { label: "Dostava", val: d.revenue.byPayment.dostava },
                      { label: "Drugo", val: d.revenue.byPayment.drugo },
                    ].filter((r) => r.val > 0).map((r) => (
                      <div key={r.label} className="dov-stat-row">
                        <span className="dov-stat-lbl">{r.label}</span>
                        <span className="dov-stat-bar-wrap">
                          <span className="dov-stat-bar">
                            <span style={{ width: `${d.revenue.byPayment.total > 0 ? Math.round(r.val / d.revenue.byPayment.total * 100) : 0}%`, background: "var(--dov-accent)" }} />
                          </span>
                          <span className="dov-stat-pct">{d.revenue.byPayment.total > 0 ? (r.val / d.revenue.byPayment.total * 100).toFixed(2) : "0.00"}%</span>
                        </span>
                        <span className="dov-stat-val mono">{fmt(r.val)}</span>
                      </div>
                    ))}
                    <div className="dov-stat-subtotal">
                      <span>Ukupno plaćanja</span>
                      <span className="mono">{fmt(d.revenue.byPayment.total)}</span>
                    </div>
                    {d.revenue.extraIncome > 0 && (
                      <div className="dov-stat-row ok">
                        <span className="dov-stat-lbl">Dodatni prihodi</span>
                        <span className="dov-stat-bar-wrap">
                          <span className="dov-stat-pct">{revenueTotal > 0 ? (d.revenue.extraIncome / revenueTotal * 100).toFixed(2) : "0.00"}%</span>
                        </span>
                        <span className="dov-stat-val mono">+{fmt(d.revenue.extraIncome)}</span>
                      </div>
                    )}
                    <div className="dov-stat-total">
                      <span>TOTAL</span>
                      <span className="mono">{fmt(d.revenue.totalWithExtra)} <span style={{ color: "var(--dov-muted)", fontWeight: 400 }}>100%</span></span>
                    </div>
                    {(d.revenue.discrepancyRecords?.length ?? 0) > 0 && (
                      <button
                        className="dov-discrepancy-btn"
                        onClick={() => setDiscrepancyOpen(true)}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                          <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        Razlika prihoda po grupama / prihodi po načinu plaćanja ({d.revenue.discrepancyRecords.length} zapisa)
                        <span className={`dov-discrepancy-val ${(d.revenue.discrepancy ?? 0) > 0 ? "pos" : "neg"}`}>
                          {(d.revenue.discrepancy ?? 0) > 0 ? "+" : ""}{fmt(d.revenue.discrepancy ?? 0)} {currency}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>}

              {/* ── Troškovi ─────────────────────────────────────────────── */}
              {canViewExpenses && <div className="dov-stat-panel">
                <div className="dov-stat-panel-head rose" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Troškovi</span>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <button className="dov-stat-link-btn" onClick={() => navigateToFinance(undefined, [d.month])}>Finansije →</button>
                    <button
                      className="dov-btn-ghost"
                      title="Otvori u novom tabu"
                      onClick={() => openFinanceInNewTab(undefined, [d.month])}
                      style={{ padding: "3px 6px" }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="dov-stat-body">
                  {/* Direktni */}
                  <div className="dov-stat-group">
                    <div className="dov-stat-group-lbl rose">Direktni troškovi</div>
                    {(d.expenses.direct ?? []).map((c) => (
                      <a key={c.id} className={`dov-stat-row ${c.total === 0 ? "zero" : ""}`}
                        href={buildFinanceUrl(c.id, [d.month])}
                        style={{ cursor: "pointer", textDecoration: "none", color: "inherit", display: "flex" }}
                        onClick={(e) => { e.preventDefault(); navigateToFinance(c.id, [d.month]); }}>
                        <span className="dov-stat-lbl">{c.name}</span>
                        <span className="dov-stat-bar-wrap">
                          <span className="dov-stat-bar">
                            <span style={{ width: `${d.expenses.directTotal > 0 ? Math.round(c.total / d.expenses.directTotal * 100) : 0}%`, background: "var(--dov-rose)" }} />
                          </span>
                          <span className="dov-stat-pct">{d.expenses.directTotal > 0 ? Math.round(c.total / d.expenses.directTotal * 100) : 0}%</span>
                        </span>
                        <span className="dov-stat-val mono">
                          {fmt(c.total)} {currency}
                          {expensesGrandTotal > 0 && c.total > 0 && (
                            <span style={{ fontSize: 11, color: "var(--dov-muted, #9ca3af)", fontWeight: 400, marginLeft: 4 }}>
                              ({Math.round(c.total / expensesGrandTotal * 100)}% tot.)
                            </span>
                          )}
                        </span>
                      </a>
                    ))}
                    <div className="dov-stat-subtotal rose">
                      <span>DIREKTNI</span>
                      <span className="mono">{fmt(d.expenses.directTotal)} {currency}</span>
                    </div>
                  </div>
                  {/* Operativni */}
                  <div className="dov-stat-group">
                    <div className="dov-stat-group-lbl violet">Operativni troškovi</div>
                    {(d.expenses.operating ?? []).map((c) => (
                      <a key={c.id} className={`dov-stat-row ${c.total === 0 ? "zero" : ""}`}
                        href={buildFinanceUrl(c.id, [d.month])}
                        style={{ cursor: "pointer", textDecoration: "none", color: "inherit", display: "flex" }}
                        onClick={(e) => { e.preventDefault(); navigateToFinance(c.id, [d.month]); }}>
                        <span className="dov-stat-lbl">{c.name}</span>
                        <span className="dov-stat-bar-wrap">
                          <span className="dov-stat-bar">
                            <span style={{ width: `${d.expenses.operatingTotal > 0 ? Math.round(c.total / d.expenses.operatingTotal * 100) : 0}%`, background: "var(--dov-violet)" }} />
                          </span>
                          <span className="dov-stat-pct">{d.expenses.operatingTotal > 0 ? Math.round(c.total / d.expenses.operatingTotal * 100) : 0}%</span>
                        </span>
                        <span className="dov-stat-val mono">
                          {fmt(c.total)} {currency}
                          {expensesGrandTotal > 0 && c.total > 0 && (
                            <span style={{ fontSize: 11, color: "var(--dov-muted, #9ca3af)", fontWeight: 400, marginLeft: 4 }}>
                              ({Math.round(c.total / expensesGrandTotal * 100)}% tot.)
                            </span>
                          )}
                        </span>
                      </a>
                    ))}
                    <div className="dov-stat-subtotal violet">
                      <span>OPERATIVNI</span>
                      <span className="mono">{fmt(d.expenses.operatingTotal)} {currency}</span>
                    </div>
                  </div>
                  {/* Zarade zaposlenih (realne plate, bez pozajmnica) — opciono, po klijentu */}
                  {showEmployeeSalaries && (
                    <div className="dov-stat-group">
                      <div className="dov-stat-group-lbl amber">Zarade zaposlenih</div>
                      <a className={`dov-stat-row ${salaryCost === 0 ? "zero" : ""}`}
                        href={buildFinanceUrl(undefined, [d.month])}
                        style={{ cursor: "pointer", textDecoration: "none", color: "inherit", display: "flex" }}
                        onClick={(e) => { e.preventDefault(); navigateToFinance(undefined, [d.month]); }}>
                        <span className="dov-stat-lbl">
                          Neto plate (bez pozajmnica)
                          {(salaryActiveCount + salaryFrozenCount) > 0 && (
                            <span style={{ fontSize: 10.5, color: "var(--dov-muted)", fontWeight: 400, marginLeft: 6 }}>
                              {salaryActiveCount} aktivnih{salaryFrozenCount > 0 ? `, ${salaryFrozenCount} zamrznuto` : ""}
                            </span>
                          )}
                        </span>
                        <span className="dov-stat-bar-wrap">
                          <span className="dov-stat-bar">
                            <span style={{ width: `${expensesGrandTotal > 0 ? Math.round(salaryCost / expensesGrandTotal * 100) : 0}%`, background: "var(--dov-amber)" }} />
                          </span>
                          <span className="dov-stat-pct">{expensesGrandTotal > 0 ? Math.round(salaryCost / expensesGrandTotal * 100) : 0}%</span>
                        </span>
                        <span className="dov-stat-val mono">
                          {fmt(salaryCost)} {currency}
                          {expensesGrandTotal > 0 && salaryCost > 0 && (
                            <span style={{ fontSize: 11, color: "var(--dov-muted, #9ca3af)", fontWeight: 400, marginLeft: 4 }}>
                              ({Math.round(salaryCost / expensesGrandTotal * 100)}% tot.)
                            </span>
                          )}
                        </span>
                      </a>
                      <div className="dov-stat-subtotal amber">
                        <span>ZARADE</span>
                        <span className="mono">{fmt(salaryCost)} {currency}</span>
                      </div>
                    </div>
                  )}
                  <div className="dov-stat-total">
                    <span>TOTAL</span>
                    <span className="mono">{fmt(expensesGrandTotal)} {currency}</span>
                  </div>
                </div>

                {/* Profit summary */}
                <div className="dov-stat-profit-box">
                  <div className="dov-stat-profit-row">
                    <span>Prihodi</span>
                    <span className="mono ok">{fmt(d.revenue.totalWithExtra)}</span>
                  </div>
                  <div className="dov-stat-profit-row">
                    <span>Direktni troškovi</span>
                    <span className="mono rose">−{fmt(d.expenses.directTotal)}</span>
                  </div>
                  <div className="dov-stat-profit-row">
                    <span>Operativni troškovi</span>
                    <span className="mono rose">−{fmt(d.expenses.operatingTotal)}</span>
                  </div>
                  {showEmployeeSalaries && (
                    <div className="dov-stat-profit-row">
                      <span>Zarade zaposlenih (bez pozajmnica)</span>
                      <span className="mono rose">−{fmt(salaryCost)}</span>
                    </div>
                  )}
                  <div className="dov-stat-profit-row bold">
                    <span>Neto rezultat</span>
                    <span className={`mono ${netProfit >= 0 ? "ok" : "rose"}`}>{fmt(netProfit)} {currency}</span>
                  </div>
                  {detailGoodsStock > 0 && (
                    <>
                      <div className="dov-stat-profit-row">
                        <span>Stanje magacina</span>
                        <span className="mono ok">+{fmt(detailGoodsStock)}</span>
                      </div>
                      <div className={`dov-stat-profit-row bold ${(netProfit + detailGoodsStock) >= 0 ? "" : "neg"}`}>
                        <span>Stvarni profit</span>
                        <span className={`mono ${(netProfit + detailGoodsStock) >= 0 ? "ok" : "rose"}`}>
                          {fmt(netProfit + detailGoodsStock)} {currency}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="dov-stat-profit-row small">
                    <span>Profit margin</span>
                    <span className="mono">{d.revenue.totalWithExtra > 0 ? Math.round(netProfit / d.revenue.totalWithExtra * 100) : 0}%</span>
                  </div>
                </div>
              </div>}

              {/* ── Rashod (Investicije + Krediti) ───────────────────────── */}
              {canViewProfit && <div className="dov-stat-panel">
                <div className="dov-stat-panel-head warn">Rashod</div>
                <div className="dov-stat-body">
                  {(d.investments?.length ?? 0) > 0 && (
                    <div className="dov-stat-group">
                      <div className="dov-stat-group-lbl">
                        <label className="dov-check" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={inclInvestments} onChange={(e) => setInclInvestments(e.target.checked)} />
                          Investicije
                        </label>
                      </div>
                      {(d.investments ?? []).map((inv, i) => (
                        <div key={i} className="dov-rashod-row">
                          <span className="dov-rashod-name">{inv.name}</span>
                          <span className="dov-rashod-date">{finance.detail!.month}</span>
                          <span className={`dov-rashod-status ${inv.hasRecord ? "paid" : "pending"}`}>
                            {inv.hasRecord ? "✓" : "~"} {fmt(inv.amount)}
                          </span>
                          <span className="dov-rashod-neg mono">−{fmt(inv.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {(d.credits?.length ?? 0) > 0 && (
                    <div className="dov-stat-group">
                      <div className="dov-stat-group-lbl">
                        <label className="dov-check" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={inclCredits} onChange={(e) => setInclCredits(e.target.checked)} />
                          Krediti
                        </label>
                      </div>
                      {(d.credits ?? []).map((cr, i) => {
                        const isForeign = !!cr.originalCurrency && cr.originalCurrency !== finance.baseCurrency;
                        return (
                        <div key={i} className="dov-rashod-row">
                          <span className="dov-rashod-name">
                            {cr.name}
                            {isForeign && (
                              <span className="dov-rashod-fx" title={cr.rateMissing ? "Kurs nije podešen — koristi se 1:1. Podesi kurs u Podešavanjima." : undefined}>
                                {" "}({fmt(cr.originalAmount ?? cr.amount)} {cr.originalCurrency}{cr.rateMissing ? " ⚠" : ""})
                              </span>
                            )}
                          </span>
                          <span className="dov-rashod-date">{finance.detail!.month}</span>
                          {cr.installmentNumber && (
                            <span className="dov-rashod-rata">Rata {cr.installmentNumber}/{cr.installmentsCount}</span>
                          )}
                          <span className={`dov-rashod-status ${cr.hasRecord ? "paid" : "pending"}`}>
                            {cr.hasRecord ? "✓" : "~"} {fmt(cr.amount)}
                          </span>
                          <span className="dov-rashod-neg mono">−{fmt(cr.amount)}</span>
                        </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="dov-stat-subtotal rose">
                    <span>UKUPNO RASHOD</span>
                    <span className="mono">−{fmt(rashodTotal)} {currency}</span>
                  </div>

                  <div className={`dov-stat-final ${finalProfit >= 0 ? "ok" : "neg"}`}>
                    <span>FINALNI PROFIT</span>
                    <span className="mono">{fmt(finalProfit)} {currency}</span>
                  </div>
                </div>
              </div>}
            </div>
          </div>
        );
      })()}

      {/* ── Reminders strip ─────────────────────────────────────────────── */}
      {canViewDashboard && <div className="dov-reminders">
        {/* Expiring contracts */}
        <div className="dov-rem">
          <div className="dov-rem-ic warn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <path d="M14 3H6v18h12V7z" /><path d="M14 3v4h4" />
            </svg>
          </div>
          <div className="dov-rem-body">
            <div className="dov-rem-label">Isticanje ugovora</div>
            <div className="dov-rem-meta">
              {contracts.length > 0
                ? `${contracts[0].firstName} ${contracts[0].lastName} · za ${contracts[0].daysUntil}d`
                : "Nema isticanja"}
            </div>
          </div>
          <div className={`dov-rem-count ${contracts.length === 0 ? "zero" : ""}`}>{contracts.length}</div>
        </div>

        {/* Sanitary booklets */}
        <div className="dov-rem">
          <div className="dov-rem-ic rose">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <path d="M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6z" />
            </svg>
          </div>
          <div className="dov-rem-body">
            <div className="dov-rem-label">Sanitarne knjižice</div>
            <div className="dov-rem-meta">
              {sanitary.length > 0
                ? `${sanitary[0].firstName} ${sanitary[0].lastName} · za ${sanitary[0].daysUntil}d`
                : "Nema isticanja"}
            </div>
          </div>
          <div className={`dov-rem-count ${sanitary.length === 0 ? "zero" : ""}`}>{sanitary.length}</div>
        </div>

        {/* Loans */}
        <div className="dov-rem">
          <div className="dov-rem-ic accent">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18" />
            </svg>
          </div>
          <div className="dov-rem-body">
            <div className="dov-rem-label">Krediti · ovaj mjesec</div>
            <div className="dov-rem-meta">
              {finance?.loansThisMonth.count
                ? `${finance.loansThisMonth.count} rata · ${fmt(finance.loansThisMonth.total)} ${currency}`
                : "Nema aktivnih rata"}
            </div>
          </div>
          <div className={`dov-rem-count ${!finance?.loansThisMonth.count ? "zero" : ""}`}>
            {finance?.loansThisMonth.count ?? 0}
          </div>
        </div>

        {/* Birthdays */}
        <div className="dov-rem">
          <div className="dov-rem-ic violet">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" />
            </svg>
          </div>
          <div className="dov-rem-body">
            <div className="dov-rem-label">Predstojeći rođendani</div>
            <div className="dov-rem-meta">
              {birthdays.length > 0
                ? `${birthdays[0].firstName} ${birthdays[0].lastName}`
                : "Nema predstojecih"}
            </div>
          </div>
          <div className={`dov-rem-count ${birthdays.length === 0 ? "zero" : ""}`}>{birthdays.length}</div>
        </div>

        {/* Active team */}
        <div className="dov-rem">
          <div className="dov-rem-ic ok">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <circle cx="9" cy="8" r="3.5" /><path d="M2 20a7 7 0 0 1 14 0" />
              <path d="M16 4a3.5 3.5 0 0 1 0 7M22 20a6 6 0 0 0-5-6" />
            </svg>
          </div>
          <div className="dov-rem-body">
            <div className="dov-rem-label">Aktivni tim</div>
            <div className="dov-rem-meta">Zaposleni · sada aktivni</div>
          </div>
          <div className="dov-rem-count">{finance?.activeUsersCount ?? "—"}</div>
        </div>
      </div>}

      {/* ── Trend period toolbar (controls KPIs, chart, breakdown below) ─── */}
      {(canViewBreakdown || canViewCharts) && (
        <div className="dov-trend-toolbar">
          <div className="dov-trend-toolbar-label">
            <span className="dov-trend-toolbar-title">Pregled trendova</span>
            <span className="dov-trend-toolbar-sub">
              {isCustomMode
                ? customMonths.length > 0
                  ? customMonths.map((m) => dayjs(m).format("MMM YY")).join(", ")
                  : "Izaberi mesece"
                : `Poslednjih ${period} meseci`}
            </span>
          </div>
          <div className="dov-trend-toolbar-controls">
            <div className="dov-seg">
              {[3, 6, 12].map((p) => (
                <button
                  key={p}
                  className={!isCustomMode && period === p ? "active" : ""}
                  onClick={() => { setIsCustomMode(false); setPeriod(p); }}
                >
                  {p}M
                </button>
              ))}
              <button
                className={isCustomMode ? "active" : ""}
                onClick={() => {
                  setIsCustomMode(true);
                  if (customMonths.length === 0) {
                    setCustomMonths([
                      dayjs().format("YYYY-MM"),
                      dayjs().subtract(1, "month").format("YYYY-MM"),
                    ]);
                  }
                }}
              >
                Custom
              </button>
            </div>
            {isCustomMode && (
              <Select
                mode="multiple"
                value={customMonths}
                onChange={setCustomMonths}
                placeholder="Izaberi mesece"
                style={{ minWidth: 240 }}
                maxTagCount="responsive"
                className="dov-loc-select"
                options={monthOptions}
                allowClear
              />
            )}
          </div>
        </div>
      )}

      {/* ── KPI row ─────────────────────────────────────────────────────── */}
      {canViewBreakdown && <div className="dov-kpi-row">
        {kpis.map((k) => {
          const sp = buildSparkPath(k.spark);
          const last = sp?.pts[sp.pts.length - 1];
          const deltaUp = k.delta !== null && k.delta !== undefined ? k.delta >= 0 : null;
          return (
            <div className="dov-kpi" key={k.label}>
              <div className="dov-kpi-head">
                <span className="dov-kpi-lbl">{k.label} <span className="dov-kpi-period">{k.sublabel}</span></span>
                {k.delta !== null && k.delta !== undefined && (
                  <span className={`dov-kpi-chip ${deltaUp ? "up" : "down"}`}>
                    {deltaUp ? "▲" : "▼"} {Math.abs(Math.round(k.delta))}%
                  </span>
                )}
              </div>
              <div className="dov-kpi-val">
                {k.isRSD ? fmt(k.value) : k.value}
                <span className="dov-kpi-cur">{k.isRSD ? currency : (k as any).suffix}</span>
              </div>
              {sp && (
                <svg className="dov-kpi-spark" viewBox="0 0 120 32" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id={k.gradId} x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0" stopColor={k.color} stopOpacity="0.22" />
                      <stop offset="1" stopColor={k.color} stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d={sp.area} fill={`url(#${k.gradId})`} />
                  <path d={sp.line} stroke={k.color} strokeWidth="1.6" fill="none" strokeLinejoin="round" strokeLinecap="round" />
                  {last && <circle cx={last.x.toFixed(1)} cy={last.y.toFixed(1)} r="2.6" fill={k.color} />}
                </svg>
              )}
              <div className="dov-kpi-foot">
                <span>{months[0]?.label} → {months[months.length - 1]?.label}</span>
                {k.isRSD && months.length >= 2 && (
                  <span className="dov-mono">
                    {fmtK(k.spark[0])} → {fmtK(k.spark[k.spark.length - 1])}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>}

      {/* ── Big chart ────────────────────────────────────────────────────── */}
      {canViewCharts && <section className="dov-chart-card">
        <div className="dov-chart-head">
          <div>
            <h2 className="dov-chart-title">Zarada · zadnjih {period} mjeseci</h2>
            <div className="dov-chart-sub">
              Prihodi, troškovi i profit po mjesecima · {months[0]?.full} – {months[months.length - 1]?.full}
            </div>
          </div>
          <div className="dov-legend">
            <span className="dov-lg"><span className="dov-sw" style={{ background: "oklch(0.55 0.16 265)" }} />Prihodi</span>
            {apartmentRevenue?.hasData && (
              <span className="dov-lg"><span className="dov-sw" style={{ background: "oklch(0.58 0.14 180)" }} />Apartmani</span>
            )}
            <span className="dov-lg"><span className="dov-sw" style={{ background: "oklch(0.82 0.07 22)" }} />Troškovi</span>
            <span className="dov-lg dov-lg-line"><span className="dov-sw" style={{ background: "oklch(0.62 0.13 158)" }} />Op. profit</span>
            <span className="dov-lg dov-lg-line"><span className="dov-sw" style={{ background: "oklch(0.55 0.18 305)" }} />Fin. profit</span>
            {hasGoodsData && (
              <span className="dov-lg dov-lg-line"><span className="dov-sw" style={{ background: "oklch(0.62 0.12 200)" }} />Stanje robe</span>
            )}
          </div>
        </div>
        <div className="dov-chart-checks">
          <label className="dov-check">
            <input type="checkbox" checked={inclInvestments} onChange={(e) => setInclInvestments(e.target.checked)} />
            Uključi investicije
            {sumInvesticije > 0 && <span className="dov-check-amt">−{fmt(sumInvesticije)} {currency}</span>}
          </label>
          <label className="dov-check">
            <input type="checkbox" checked={inclCredits} onChange={(e) => setInclCredits(e.target.checked)} />
            Uključi kredite
            {sumKrediti > 0 && <span className="dov-check-amt">−{fmt(sumKrediti)} {currency}</span>}
          </label>
        </div>

        <div className="dov-chart-wrap" style={{ position: "relative" }}>
          {hoveredMonth !== null && monthsWithApt[hoveredMonth] && (() => {
            const m = monthsWithApt[hoveredMonth];
            return (
              <div
                className="dov-tip"
                style={{ left: tipPos.x, top: tipPos.y, transform: "translate(-50%, -110%)" }}
              >
                <div className="dov-tip-mo">{m.full}</div>
                <div className="dov-tip-row">
                  <span><span className="dov-sw" style={{ background: "oklch(0.55 0.16 265)", display: "inline-block" }} />Prihod</span>
                  <span>{fmt(m.prihod)}</span>
                </div>
                {m.prihodApt > 0 && (
                  <div className="dov-tip-row">
                    <span><span className="dov-sw" style={{ background: "oklch(0.58 0.14 180)", display: "inline-block" }} />Apartmani</span>
                    <span>+{fmt(m.prihodApt)}</span>
                  </div>
                )}
                {m.prihodApt > 0 && (
                  <div className="dov-tip-row" style={{ fontSize: 10, color: "oklch(0.75 0.01 260)" }}>
                    <span>Ukupno prihod</span>
                    <span>{fmt(m.prihodTotal)}</span>
                  </div>
                )}
                <div className="dov-tip-row">
                  <span><span className="dov-sw" style={{ background: "oklch(0.82 0.07 22)", display: "inline-block" }} />Troškovi</span>
                  <span>{fmt(m.trosak)}</span>
                </div>
                <div className="dov-tip-row dov-tip-sep">
                  <span><span className="dov-sw" style={{ background: "oklch(0.62 0.13 158)", display: "inline-block" }} />Op. profit</span>
                  <span>{fmt(m.prihodTotal - m.trosak)}</span>
                </div>
                {(m.investicije || 0) > 0 && (
                  <div className="dov-tip-row">
                    <span>Investicije</span>
                    <span>−{fmt(m.investicije)}</span>
                  </div>
                )}
                {(m.krediti || 0) > 0 && (
                  <div className="dov-tip-row">
                    <span>Krediti</span>
                    <span>−{fmt(m.krediti)}</span>
                  </div>
                )}
                {((m.investicije || 0) > 0 || (m.krediti || 0) > 0) && (
                  <div className="dov-tip-row dov-tip-sep" style={{ fontWeight: 600 }}>
                    <span><span className="dov-sw" style={{ background: "oklch(0.55 0.18 305)", display: "inline-block" }} />Fin. profit</span>
                    <span style={{ color: (m.profit - (inclInvestments ? m.investicije||0 : 0) - (inclCredits ? m.krediti||0 : 0)) >= 0 ? "oklch(0.62 0.13 158)" : "oklch(0.62 0.18 22)" }}>
                      {fmt(m.profit - (inclInvestments ? m.investicije||0 : 0) - (inclCredits ? m.krediti||0 : 0))}
                    </span>
                  </div>
                )}
                <div className="dov-tip-row">
                  <span>Margina</span>
                  <span>{Math.round(m.margin * 100)}%</span>
                </div>
                {hasGoodsData && (goodsClosingStockByMonth[m.month] ?? 0) > 0 && (
                  <div className="dov-tip-row dov-tip-sep">
                    <span><span className="dov-sw" style={{ background: "oklch(0.62 0.12 200)", display: "inline-block" }} />Stanje robe</span>
                    <span>{fmt(goodsClosingStockByMonth[m.month] || 0)}</span>
                  </div>
                )}
              </div>
            );
          })()}

          <svg
            ref={chartSvgRef}
            className="dov-chart-svg"
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            onMouseLeave={() => setHoveredMonth(null)}
          >
            <defs>
              <linearGradient id="dov-cg-prihod" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="oklch(0.62 0.16 265)" />
                <stop offset="1" stopColor="oklch(0.5 0.18 280)" />
              </linearGradient>
              <linearGradient id="dov-cg-trosak" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="oklch(0.85 0.06 22)" />
                <stop offset="1" stopColor="oklch(0.78 0.08 22)" />
              </linearGradient>
              <linearGradient id="dov-cg-profit-fill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="oklch(0.62 0.13 158)" stopOpacity="0.18" />
                <stop offset="1" stopColor="oklch(0.62 0.13 158)" stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Y grid + labels */}
            {yTicks.map((t) => (
              <g key={t}>
                <line x1={padL} x2={W - padR} y1={yS(t)} y2={yS(t)}
                  stroke="oklch(0.93 0.006 260)" strokeWidth="1"
                  strokeDasharray={t === 0 ? undefined : "3 4"} />
                <text x={padL - 10} y={yS(t) + 4} textAnchor="end" fontSize="10"
                  fontFamily="JetBrains Mono,monospace" fill="oklch(0.58 0.012 260)">
                  {t === 0 ? "0" : fmtK(t)}
                </text>
              </g>
            ))}

            {/* Bars */}
            {monthsWithApt.map((m, i) => {
              const cx = padL + colW * i + colW / 2;
              const x1 = cx - barW - 3;
              const x2 = cx + 3;
              const yP  = yS(m.prihodTotal);
              const yPB = yS(m.prihod); // base prihod (bottom part of revenue bar)
              const yT  = yS(m.trosak);
              const y0  = yS(0);
              const hasApt = m.prihodApt > 0;
              return (
                <g key={m.month}>
                  {/* Revenue bar: base (blue) + apartment stacked on top (teal) */}
                  <rect x={x1} y={yPB} width={barW} height={y0 - yPB} rx="3" fill="url(#dov-cg-prihod)" opacity={hoveredMonth === null || hoveredMonth === i ? 1 : 0.4} />
                  {hasApt && (
                    <rect x={x1} y={yP} width={barW} height={yPB - yP} rx="3"
                      fill="oklch(0.58 0.14 180)" opacity={hoveredMonth === null || hoveredMonth === i ? 0.9 : 0.35} />
                  )}
                  {/* Cost bar */}
                  <rect x={x2} y={yT} width={barW} height={y0 - yT} rx="3" fill="url(#dov-cg-trosak)" opacity={hoveredMonth === null || hoveredMonth === i ? 1 : 0.4} />
                  <text x={cx} y={H - padB + 18} textAnchor="middle" fontSize="11"
                    fontFamily="Inter Tight,sans-serif"
                    fill={m.current ? "oklch(0.18 0.012 260)" : "oklch(0.52 0.012 260)"}
                    fontWeight={m.current ? "600" : "500"}>
                    {m.label}
                  </text>
                  {m.current && (
                    <rect x={cx - 22} y={H - padB + 22} width={44} height={3} rx="1.5" fill="oklch(0.55 0.16 265)" />
                  )}
                </g>
              );
            })}

            {/* Profit area + line */}
            {profitPts.length >= 2 && (
              <>
                <path d={profitArea} fill="url(#dov-cg-profit-fill)" />
                <path d={profitLine} stroke="oklch(0.62 0.13 158)" strokeWidth="2"
                  fill="none" strokeLinejoin="round" strokeLinecap="round" strokeDasharray="5 3" />
                {profitPts.map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r={i === profitPts.length - 1 ? 3.5 : 2.5}
                    fill="#fff" stroke="oklch(0.62 0.13 158)" strokeWidth="1.8"
                    opacity={hoveredMonth === null || hoveredMonth === i ? 1 : 0.35} />
                ))}
                {/* Final profit line (solid, thicker) */}
                {finalProfitLine && (
                  <path d={finalProfitLine} stroke="oklch(0.55 0.18 305)" strokeWidth="2.5"
                    fill="none" strokeLinejoin="round" strokeLinecap="round" />
                )}
                {finalProfitPts.map((p, i) => (
                  <circle key={`fp-${i}`} cx={p.x} cy={p.y} r={i === finalProfitPts.length - 1 ? 4.5 : 3}
                    fill="#fff" stroke="oklch(0.55 0.18 305)" strokeWidth="2"
                    opacity={hoveredMonth === null || hoveredMonth === i ? 1 : 0.35} />
                ))}
                {/* Goods stock line */}
                {hasGoodsData && goodsStockLine && (
                  <path d={goodsStockLine} stroke="oklch(0.62 0.12 200)" strokeWidth="2"
                    fill="none" strokeLinejoin="round" strokeLinecap="round" strokeDasharray="4 3" />
                )}
                {hasGoodsData && goodsStockPts.map((p, i) => (
                  <circle key={`gs-${i}`} cx={p.x} cy={p.y} r={i === goodsStockPts.length - 1 ? 3.5 : 2.5}
                    fill="#fff" stroke="oklch(0.62 0.12 200)" strokeWidth="1.8"
                    opacity={hoveredMonth === null || hoveredMonth === i ? 1 : 0.35} />
                ))}
              </>
            )}

            {/* Hover zones — rendered last so they sit above all visual elements */}
            {monthsWithApt.map((m, i) => {
              const cx = padL + colW * i + colW / 2;
              return (
                <rect
                  key={`hz-${m.month}`}
                  x={padL + colW * i} y={padT} width={colW} height={innerH}
                  fill="transparent" style={{ cursor: "pointer" }}
                  onMouseEnter={() => {
                    setHoveredMonth(i);
                    const svgEl = chartSvgRef.current;
                    if (svgEl) {
                      const rect = svgEl.getBoundingClientRect();
                      const scaleX = rect.width / W;
                      const scaleY = rect.height / H;
                      setTipPos({
                        x: cx * scaleX,
                        y: yS(Math.max(m.prihodTotal, m.trosak)) * scaleY,
                      });
                    }
                  }}
                />
              );
            })}
          </svg>
        </div>

        {/* Chart footer stats */}
        <div className="dov-chart-foot">
          <div className="dov-cfs"><span className="dov-cfs-lbl">Ukupan prihod {period}m</span><span className="dov-cfs-val accent">{fmt(sumPrihod)} {currency}</span></div>
          <div className="dov-cfs"><span className="dov-cfs-lbl">Ukupni troškovi {period}m</span><span className="dov-cfs-val rose">{fmt(sumTrosak)} {currency}</span></div>
          <div className="dov-cfs"><span className="dov-cfs-lbl">Op. profit {period}m</span><span className="dov-cfs-val ok">{fmt(sumProfit)} {currency}</span></div>
          {(sumInvesticije > 0 || sumKrediti > 0) && (
            <div className="dov-cfs">
              <span className="dov-cfs-lbl">Investicije + Krediti</span>
              <span className="dov-cfs-val rose">−{fmt((inclInvestments ? sumInvesticije : 0) + (inclCredits ? sumKrediti : 0))} {currency}</span>
            </div>
          )}
          <div className="dov-cfs">
            <span className="dov-cfs-lbl">Finalni profit {period}m</span>
            <span className={`dov-cfs-val ${effectiveProfit >= 0 ? "ok" : "rose"}`}>{fmt(effectiveProfit)} {currency}</span>
          </div>
          <div className="dov-cfs"><span className="dov-cfs-lbl">Fin. margina</span><span className="dov-cfs-val">{Math.round(avgFinalMargin * 100)}%</span></div>
          <div className="dov-cfs"><span className="dov-cfs-lbl">Najbolji mesec (op.)</span><span className="dov-cfs-val">{bestMonth ? `${bestMonth.label} · ${fmtK(bestMonth.profit)}` : "—"}</span></div>
          <div className="dov-cfs">
            <span className="dov-cfs-lbl">Trend (vs. {months[0]?.label})</span>
            <span className={`dov-cfs-val ${trend !== null && trend >= 0 ? "ok" : "rose"}`}>
              {trend !== null ? `${trend >= 0 ? "+" : ""}${Math.round(trend)}%` : "—"}
            </span>
          </div>
        </div>
      </section>}

      {/* ── Monthly breakdown — full width ──────────────────────────────── */}
        {canViewBreakdown && <div className="dov-panel dov-panel-full">

          <div className="dov-panel-head">
            <div>
              <h3>Mjesečni breakdown</h3>
              <div className="dov-panel-sub">Klikni na mjesec za detaljan pregled</div>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button className="dov-btn-ghost" style={{ padding: "6px 10px", fontSize: 12 }}
                onClick={() => navigateToFinance()}>
                Finansije
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
              <button
                className="dov-btn-ghost"
                title="Otvori u novom tabu"
                onClick={() => openFinanceInNewTab()}
                style={{ padding: "6px 8px" }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </button>
            </div>
          </div>
          <table className="dov-mtbl">
            <thead>
              <tr>
                <th>Mj.</th>
                <th>Prihod</th>
                <th>Troškovi</th>
                <th>Op. profit</th>
                {(sumInvesticije > 0) && <th>Invest.</th>}
                {(sumKrediti > 0) && <th>Krediti</th>}
                <th>Fin. profit</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {monthsWithApt.map((m, i) => {
                const prev = i > 0 ? monthsWithApt[i - 1] : null;
                const opProfit = m.prihodTotal - m.trosak;
                const prevOpProfit = prev ? (prev.prihodTotal - prev.trosak) : null;
                const dOp = prevOpProfit !== null && prevOpProfit !== 0 ? ((opProfit - prevOpProfit) / Math.abs(prevOpProfit)) * 100 : null;
                const inv  = inclInvestments ? (m.investicije || 0) : 0;
                const kred = inclCredits     ? (m.krediti || 0)     : 0;
                const fp   = opProfit - inv - kred;
                return (
                  <tr key={m.month} className={m.current ? "current" : ""}
                    onClick={() => navigateToFinance()}>
                    <td title={m.full}>{m.label}</td>
                    <td>
                      {fmt(m.prihodTotal)}
                      {m.prihodApt > 0 && <span style={{ fontSize: 10, color: "oklch(0.58 0.14 180)", marginLeft: 4 }}>+apt</span>}
                    </td>
                    <td>{fmt(m.trosak)}</td>
                    <td>
                      {fmt(opProfit)}{" "}
                      {dOp !== null && (
                        <span className={`dov-delta ${dOp > 1 ? "up" : dOp < -1 ? "down" : "flat"}`}>
                          {dOp > 0 ? "+" : ""}{Math.round(dOp)}%
                        </span>
                      )}
                    </td>
                    {(sumInvesticije > 0) && <td className="dov-neg-cell">{(m.investicije||0) > 0 ? `−${fmt(m.investicije)}` : "—"}</td>}
                    {(sumKrediti > 0) && <td className="dov-neg-cell">{(m.krediti||0) > 0 ? `−${fmt(m.krediti)}` : "—"}</td>}
                    <td style={{ color: fp >= 0 ? "var(--dov-ok)" : "var(--dov-rose)", fontWeight: 600 }}>
                      {fmt(fp)}
                    </td>
                    <td className={`dov-margin-pct ${(m.prihodTotal - m.trosak) < 0 ? "neg" : ""}`}>
                      {Math.round(m.margin * 100)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td>Ukupno</td>
                <td>{fmt(sumPrihod)}</td>
                <td>{fmt(sumTrosak)}</td>
                <td>{fmt(sumProfit)}</td>
                {(sumInvesticije > 0) && <td>−{fmt(sumInvesticije)}</td>}
                {(sumKrediti > 0) && <td>−{fmt(sumKrediti)}</td>}
                <td style={{ color: effectiveProfit >= 0 ? "var(--dov-ok)" : "var(--dov-rose)" }}>{fmt(effectiveProfit)}</td>
                <td>{Math.round(avgMargin * 100)}%</td>
              </tr>
            </tfoot>
          </table>
        </div>}

      {/* ── Troškovi po kategorijama — full width ────────────────────────── */}
        {canViewExpenses && <div className="dov-panel dov-panel-full">
          <div className="dov-panel-head">
            <div>
              <h3>Troškovi po kategorijama</h3>
              <div className="dov-panel-sub">{months[months.length - 1]?.full ?? ""}</div>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button className="dov-btn-ghost" style={{ padding: "6px 10px", fontSize: 12 }}
                onClick={() => navigateToFinance()}>
                Finansije
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
              <button
                className="dov-btn-ghost"
                title="Otvori u novom tabu"
                onClick={() => openFinanceInNewTab()}
                style={{ padding: "6px 8px" }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </button>
            </div>
          </div>
          {finance?.categories.direct && finance.categories.direct.length > 0 && (
            <>
              <div className="dov-cat-sect-head direct">
                <span>Direktni troškovi</span>
                <span className="dov-cat-sect-total">
                  {fmt(finance.categories.direct.reduce((s, c) => s + c.total, 0))} {currency}
                </span>
              </div>
              <div className="dov-cat-grid">
                {finance.categories.direct.map((c) => (
                  <a key={c.id} className="dov-cat" href={buildFinanceUrl(c.id)}
                    style={{ textDecoration: "none", color: "inherit" }}
                    onClick={(e) => { e.preventDefault(); navigateToFinance(c.id); }}>
                    <div className="dov-cat-ic rose">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                        <path d="M3 6h18M3 12h18M3 18h12" />
                      </svg>
                    </div>
                    <div className="dov-cat-body">
                      <div className="dov-cat-name">{c.name}</div>
                      <div className="dov-cat-meta">{c.total === 0 ? "bez troška" : "direktni"}</div>
                    </div>
                    <div className="dov-cat-amount">
                      {c.total === 0 ? <span style={{ color: "var(--dov-subtle)", fontWeight: 400 }}>0</span> : fmt(c.total)}
                      <span className="dov-cat-cur">{currency}</span>
                    </div>
                  </a>
                ))}
              </div>
            </>
          )}
          {finance?.categories.operating && finance.categories.operating.length > 0 && (
            <>
              <div className="dov-cat-sect-head operating">
                <span>Operativni troškovi</span>
                <span className="dov-cat-sect-total">
                  {fmt(finance.categories.operating.reduce((s, c) => s + c.total, 0))} {currency}
                </span>
              </div>
              <div className="dov-cat-grid">
                {finance.categories.operating.map((c) => (
                  <a key={c.id} className="dov-cat" href={buildFinanceUrl(c.id)}
                    style={{ textDecoration: "none", color: "inherit" }}
                    onClick={(e) => { e.preventDefault(); navigateToFinance(c.id); }}>
                    <div className="dov-cat-ic violet">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                        <path d="M3 6h18M3 12h18M3 18h12" />
                      </svg>
                    </div>
                    <div className="dov-cat-body">
                      <div className="dov-cat-name">{c.name}</div>
                      <div className="dov-cat-meta">{c.total === 0 ? "bez troška" : "operativni"}</div>
                    </div>
                    <div className="dov-cat-amount">
                      {c.total === 0 ? <span style={{ color: "var(--dov-subtle)", fontWeight: 400 }}>0</span> : fmt(c.total)}
                      <span className="dov-cat-cur">{currency}</span>
                    </div>
                  </a>
                ))}
              </div>
            </>
          )}
          {!finance && (
            <div className="dov-cat-empty">Učitavanje kategorija...</div>
          )}
          {finance && !finance.categories.direct.length && !finance.categories.operating.length && (
            <div className="dov-cat-empty">Nema troškova za ovaj period</div>
          )}
        </div>}

      {/* ── Bottom strip: expiring contracts + sanitary ──────────────────── */}
      {canViewPeople && <div className="dov-grid-side">
        <div className="dov-panel">
          <div className="dov-panel-head">
            <div>
              <h3>Isticanje ugovora</h3>
              <div className="dov-panel-sub">Sledećih 30 dana</div>
            </div>
            <button className="dov-btn-ghost" style={{ padding: "6px 10px", fontSize: 12 }}
              onClick={() => navigate("/users")}>
              Svi →
            </button>
          </div>
          <div className="dov-lst">
            {contracts.length === 0 && (
              <div className="dov-lst-empty">Nema ugovora koji ističu</div>
            )}
            {contracts.map((item) => (
              <div key={item.userId + item.dueDate} className="dov-lst-row"
                onClick={() => navigate(`/users/${item.userId}`)}>
                <InitialsAvatar name={`${item.firstName} ${item.lastName}`} />
                <div style={{ flex: 1 }}>
                  <div className="dov-lst-name">{item.firstName} {item.lastName}</div>
                  <div className="dov-lst-sub">{item.position ?? "Ugovor"}</div>
                </div>
                <div className={`dov-lst-due ${item.daysUntil <= 7 ? "soon" : "fine"}`}>
                  {dayjs(item.dueDate).format("DD MMM")} · za {item.daysUntil}d
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="dov-panel">
          <div className="dov-panel-head">
            <div>
              <h3>Sanitarne knjižice</h3>
              <div className="dov-panel-sub">Sledećih 30 dana</div>
            </div>
            <button className="dov-btn-ghost" style={{ padding: "6px 10px", fontSize: 12 }}
              onClick={() => navigate("/users")}>
              Sve →
            </button>
          </div>
          <div className="dov-lst">
            {sanitary.length === 0 && (
              <div className="dov-lst-empty">Nema knjižica koje ističu</div>
            )}
            {sanitary.map((item) => (
              <div key={item.userId + item.dueDate} className="dov-lst-row"
                onClick={() => navigate(`/users/${item.userId}`)}>
                <InitialsAvatar name={`${item.firstName} ${item.lastName}`} />
                <div style={{ flex: 1 }}>
                  <div className="dov-lst-name">{item.firstName} {item.lastName}</div>
                  <div className="dov-lst-sub">{item.position ?? "Sanitarna knjižica"}</div>
                </div>
                <div className={`dov-lst-due ${item.daysUntil <= 7 ? "soon" : "fine"}`}>
                  {dayjs(item.dueDate).format("DD MMM")} · za {item.daysUntil}d
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>}

      {/* ── AI Analiza ───────────────────────────────────────────────── */}
      {canViewRevenue && finance && (
        <DashboardAIAnalysis
          financeData={finance}
          apartmentData={apartmentRevenue}
          currency={currency}
          locationName={selectedLocation ? visibleLocations.find((l) => l._id === selectedLocation)?.name : undefined}
          locationId={selectedLocation ?? undefined}
          periodKey={
            isCustomMode && customMonths.length > 0
              ? customMonths.slice().sort().join("|")
              : months.map((m) => m.month).join("|")
          }
          period={
            isCustomMode && customMonths.length > 0
              ? customMonths.map((m) => dayjs(m).format("MMM YY")).join(", ")
              : selectedMonth
                ? selectedMonth.format("MMMM YYYY")
                : `zadnjih ${period} mjeseci`
          }
        />
      )}
    </div>
    {/* ── Discrepancy modal ──────────────────────────────────────────── */}
    <Modal
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="oklch(0.7 0.13 75)" strokeWidth="2" strokeLinecap="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          Razlika prihoda po grupama / po načinu plaćanja
        </span>
      }
      open={discrepancyOpen}
      onCancel={() => setDiscrepancyOpen(false)}
      footer={null}
      width={600}
      className="standardHeader"
    >
      <p style={{ fontSize: 12, color: "var(--dov-muted)", marginBottom: 12 }}>
        Zapisi gdje suma prihoda po tipovima ≠ suma po načinu plaćanja
      </p>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #eee" }}>
            <th style={{ textAlign: "left", padding: "6px 8px", color: "#888", fontWeight: 600 }}>Datum</th>
            <th style={{ textAlign: "left", padding: "6px 8px", color: "#888", fontWeight: 600 }}>Lokacija</th>
            <th style={{ textAlign: "right", padding: "6px 8px", color: "#888", fontWeight: 600 }}>Po grupama</th>
            <th style={{ textAlign: "right", padding: "6px 8px", color: "#888", fontWeight: 600 }}>Po plaćanju</th>
            <th style={{ textAlign: "right", padding: "6px 8px", color: "#888", fontWeight: 600 }}>Razlika</th>
          </tr>
        </thead>
        <tbody>
          {(finance?.detail?.revenue.discrepancyRecords ?? []).map((r, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
              <td style={{ padding: "7px 8px" }}>{dayjs(r.date).format("DD.MM.YYYY")}</td>
              <td style={{ padding: "7px 8px", color: "#666" }}>{r.location ?? "—"}</td>
              <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "monospace" }}>{fmt(r.byType)}</td>
              <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "monospace" }}>{fmt(r.byPayment)}</td>
              <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "monospace", fontWeight: 600, color: r.diff > 0 ? "oklch(0.62 0.13 158)" : "oklch(0.62 0.18 22)" }}>
                {r.diff > 0 ? "+" : ""}{r.diff.toLocaleString("sr-RS")}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: "2px solid #eee" }}>
            <td colSpan={4} style={{ padding: "8px 8px", fontWeight: 700, fontSize: 13 }}>Ukupna razlika</td>
            <td style={{ padding: "8px 8px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, fontSize: 13,
              color: (finance?.detail?.revenue.discrepancy ?? 0) > 0 ? "oklch(0.62 0.13 158)" : "oklch(0.62 0.18 22)" }}>
              {(finance?.detail?.revenue.discrepancy ?? 0) > 0 ? "+" : ""}
              {fmt(finance?.detail?.revenue.discrepancy ?? 0)} {currency}
            </td>
          </tr>
        </tfoot>
      </table>
    </Modal>
    </>
  );
};

export default DashboardOverview;
